<template>
  <div>
    <admin-nav active="documentation" />
    <b-container fluid>
      <b-row class="mb-1">
        <b-col>
          <h3>Documentation technique</h3>
          <small class="text-muted">
            Documentation versionnée avec le code (<code>api_reunibox/docs/</code>).
            Elle est livrée avec l'API : ce que vous lisez ici correspond
            toujours à la version déployée.
          </small>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="3">
          <b-card no-body>
            <b-card-body class="pb-0">
              <b-form-input
                v-model="recherche"
                size="sm"
                placeholder="Rechercher..."
                class="mb-1"
              />
            </b-card-body>
            <b-list-group flush>
              <b-list-group-item
                v-for="doc in docsFiltres"
                :key="doc.slug"
                button
                :active="courant && courant.slug === doc.slug"
                @click="ouvrir(doc.slug)"
              >
                <div class="font-weight-bold">
                  {{ doc.titre }}
                </div>
                <small
                  :class="courant && courant.slug === doc.slug ? '' : 'text-muted'"
                >
                  {{ doc.resume }}
                </small>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>

        <b-col md="9">
          <b-card>
            <div
              v-if="chargement"
              class="text-center py-3"
            >
              <b-spinner />
            </div>
            <div v-else-if="courant">
              <div class="d-flex justify-content-between align-items-start mb-1">
                <div>
                  <h4 class="mb-0">
                    {{ courant.titre }}
                  </h4>
                  <small class="text-muted">
                    {{ courant.fichier }} — mis à jour le {{ formatDate(courant.datemaj) }}
                  </small>
                </div>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="telecharger"
                >
                  <feather-icon icon="DownloadIcon" /> Télécharger le .md
                </b-button>
              </div>

              <div
                v-if="courant.sommaire && courant.sommaire.length"
                class="mb-1 pb-1 border-bottom"
              >
                <a
                  v-for="titre in courant.sommaire"
                  :key="titre.id"
                  href="#"
                  class="mr-1 small"
                  :class="titre.niveau === 3 ? 'text-muted' : ''"
                  @click.prevent="allerA(titre.id)"
                >
                  {{ titre.titre }}
                </a>
              </div>

              <!-- eslint-disable vue/no-v-html -->
              <div
                class="dom-doc-md"
                @click="interceptLien"
                v-html="courant.html"
              />
              <!-- eslint-enable vue/no-v-html -->
            </div>
            <div
              v-else
              class="text-muted"
            >
              Aucun document disponible.
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BFormInput,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import AdminNav from './AdminNav.vue'

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BFormInput,
    BButton,
    BSpinner,
    AdminNav,
  },
  data() {
    return {
      docs: [],
      courant: null,
      recherche: '',
      chargement: false,
    }
  },
  computed: {
    docsFiltres() {
      const q = this.recherche.trim().toLowerCase()
      if (!q) return this.docs
      return this.docs.filter(d => `${d.titre} ${d.resume}`.toLowerCase().includes(q))
    },
  },
  created() {
    this.charger()
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('domAdminToken') } }
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('fr-FR')
    },
    charger() {
      this.$http.get('/admindom/docs', this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.docs = response.data.docs
            if (this.docs.length) this.ouvrir(this.docs[0].slug)
          }
        })
    },
    ouvrir(slug) {
      this.chargement = true
      this.$http.get(`/admindom/docs/${slug}`, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            const { doc } = response.data
            this.courant = doc
          }
          this.chargement = false
        })
        .catch(() => {
          this.chargement = false
        })
    },
    // Les liens entre documents sont rendus en <a data-doc="slug"> par
    // l'API : on les intercepte pour naviguer sans quitter la page.
    interceptLien(event) {
      const slug = event.target && event.target.dataset ? event.target.dataset.doc : null
      if (!slug) return
      event.preventDefault()
      this.ouvrir(slug)
    },
    allerA(id) {
      const cible = document.getElementById(id)
      if (cible) cible.scrollIntoView({ behavior: 'smooth' })
    },
    telecharger() {
      const blob = new Blob([this.courant.markdown], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = this.courant.fichier
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style lang="scss">
/* Rendu du Markdown : styles volontairement non scopés, le HTML injecté
   par v-html ne porte pas l'attribut de scope du composant. */
.dom-doc-md {
  h1 {
    font-size: 1.5rem;
    margin-top: 1.5rem;

    &:first-child {
      margin-top: 0;
    }
  }

  h2 {
    font-size: 1.25rem;
    margin-top: 1.75rem;
    padding-bottom: 0.25rem;
    border-bottom: 1px solid rgba(34, 41, 47, 0.1);
  }

  h3 {
    font-size: 1.05rem;
    margin-top: 1.25rem;
  }

  p,
  ul,
  ol {
    margin-bottom: 0.85rem;
  }

  code {
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    background-color: rgba(34, 41, 47, 0.06);
  }

  pre {
    padding: 0.85rem;
    border-radius: 0.35rem;
    background-color: #f5f5f7;
    overflow-x: auto;

    code {
      padding: 0;
      background: none;
    }
  }

  blockquote {
    padding: 0.5rem 0.85rem;
    border-left: 3px solid #7367f0;
    background-color: rgba(115, 103, 240, 0.06);
  }

  .doc-table {
    overflow-x: auto;
    margin-bottom: 0.85rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 0.4rem 0.6rem;
    border: 1px solid rgba(34, 41, 47, 0.1);
    vertical-align: top;
  }

  th {
    background-color: rgba(34, 41, 47, 0.04);
    text-align: left;
  }
}
</style>
